import type { ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Clock,
  Crown,
  Flame,
  Lightning,
  Lock,
  Medal,
  Sword,
  Trophy,
  type IconProps,
} from '@phosphor-icons/react';
import { practiceApi } from './api';
import type { BadgeListItem } from './types';
import { FixedNav, Caption, Card, Button, PulseDot } from '@/ui';

/**
 * Page collection badges Practice Arena (Salve P1 T7).
 *
 * Affiche la liste des badges seed (9 minimum) avec leur état unlocked/locked
 * + date d'unlock + game_id de la partie déclencheuse.
 *
 * Route : /practice-arena/badges
 *
 * Le mapping `GLYPH_TO_ICON` traduit la chaîne `icon_glyph` côté DB (cf.
 * migration 018) vers un composant Phosphor. `SwordCross` n'existe pas dans
 * Phosphor → fallback `Sword`. Tout glyph inconnu → fallback `Trophy`.
 */

type PhosphorIcon = ComponentType<IconProps>;

const GLYPH_TO_ICON: Record<string, PhosphorIcon> = {
  Trophy,
  Sword,
  SwordCross: Sword,
  Lightning,
  Crown,
  Medal,
  Flame,
  Clock,
};

function resolveBadgeIcon(glyph: string): PhosphorIcon {
  return GLYPH_TO_ICON[glyph] ?? Trophy;
}

export function BadgesPage(): JSX.Element {
  const navigate = useNavigate();
  const badgesQ = useQuery({
    queryKey: ['practice', 'badges'],
    queryFn: () => practiceApi.listBadges(),
  });

  return (
    <div className="min-h-screen bg-ink-900 text-paper-50">
      <FixedNav />
      <main className="pt-20 pb-12 px-4 md:px-8 max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <PulseDot color="var(--orange-500)" />
          <Caption color="orange">Badges</Caption>
        </div>
        <h1 className="text-heading-1 font-display font-semibold mb-1">Ta collection</h1>
        <p className="text-ink-300 text-body-lg italic mb-8">
          Trace écrite de tes exploits dans l'Arène.
        </p>

        {badgesQ.isLoading && (
          <p className="text-ink-300" data-testid="loading">Chargement…</p>
        )}
        {badgesQ.isError && (
          <p className="text-coral-500" data-testid="error">Erreur de chargement.</p>
        )}
        {badgesQ.data && (
          <>
            <p className="text-body text-ink-300 mb-6" data-testid="counter">
              {badgesQ.data.unlocked_count} / {badgesQ.data.count} débloqués
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {badgesQ.data.badges.map((b) => (
                <BadgeCard key={b.slug} badge={b} />
              ))}
            </div>
          </>
        )}

        <div className="mt-8">
          <Button variant="secondary" onClick={() => navigate('/practice-arena')}>
            Retour à L'Arène
          </Button>
        </div>
      </main>
    </div>
  );
}

function BadgeCard({ badge }: { badge: BadgeListItem }): JSX.Element {
  const Icon: PhosphorIcon = badge.unlocked ? resolveBadgeIcon(badge.icon_glyph) : Lock;
  return (
    <div data-testid={`badge-${badge.slug}`}>
      <Card
        padding="default"
        className={badge.unlocked ? 'border-orange-500/60 bg-orange-500/[0.05]' : 'opacity-50'}
      >
        <div className="flex items-start gap-3">
          <div
            className={`flex items-center justify-center w-12 h-12 rounded-lg ${
              badge.unlocked ? 'bg-orange-500/20 text-orange-500' : 'bg-ink-700 text-ink-300'
            }`}
            aria-hidden
            data-testid={`badge-${badge.slug}-icon`}
            data-glyph={badge.unlocked ? badge.icon_glyph : 'Lock'}
          >
            <Icon size={28} weight="fill" />
          </div>
          <div className="flex-1">
            <h3
              className={`text-body-lg font-medium ${
                badge.unlocked ? 'text-orange-500' : 'text-paper-50'
              }`}
            >
              {badge.name_fr}
            </h3>
            <p className="text-body-sm text-ink-300 mt-1">{badge.description_fr}</p>
            {badge.unlocked && badge.unlocked_at && (
              <p className="text-caption text-ink-300 mt-2 italic" data-testid={`badge-${badge.slug}-date`}>
                Débloqué {new Date(badge.unlocked_at).toLocaleDateString('fr-FR')}
              </p>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
